import React, { useRef, useState } from 'react';
import { Upload, X, File, Image } from 'lucide-react';
import type { FormField, ThemeConfig } from '../../../types/forms';

interface FileUploadFieldProps {
  field: FormField;
  value?: File | null;
  onChange?: (value: File | null) => void;
  error?: string;
  disabled?: boolean;
  themeConfig?: ThemeConfig;
}

export const FileUploadField: React.FC<FileUploadFieldProps> = ({
  field,
  value,
  onChange,
  error,
  disabled,
  themeConfig,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // mobile-ui.3.C AC2: cores derivadas do tema (padrão CheckboxField/DateField) — legível em tema claro
  const accentColor = themeConfig?.buttonBgColor || '#3b82f6';
  const textColor = themeConfig?.textColor || '#ffffff';

  const acceptedTypes = field.validation?.fileTypes?.join(',') || '*';
  const maxSize = field.validation?.maxFileSize || 10 * 1024 * 1024; // 10MB default

  const handleFileSelect = (file: File | null) => {
    if (!file) {
      onChange?.(null);
      return;
    }

    // Validar tamanho
    if (file.size > maxSize) {
      alert(`Arquivo muito grande. Máximo: ${(maxSize / 1024 / 1024).toFixed(1)}MB`);
      return;
    }

    onChange?.(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const isImage = value?.type.startsWith('image/');

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={acceptedTypes}
        onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
        disabled={disabled}
        className="sr-only"
      />

      {value ? (
        // Preview do arquivo selecionado
        <div
          className="flex items-center gap-4 p-4 border rounded-xl"
          style={{ backgroundColor: `${textColor}0D`, borderColor: `${textColor}4D` }}
        >
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${textColor}1A` }}
          >
            {isImage ? (
              <Image className="w-6 h-6" style={{ color: accentColor }} />
            ) : (
              <File className="w-6 h-6" style={{ color: textColor, opacity: 0.6 }} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: textColor }}>{value.name}</p>
            <p className="text-xs" style={{ color: textColor, opacity: 0.5 }}>
              {(value.size / 1024).toFixed(1)} KB
            </p>
          </div>

          {!disabled && (
            <button
              type="button"
              aria-label="Remover arquivo"
              onClick={() => handleFileSelect(null)}
              className="p-3 rounded-lg opacity-70 hover:opacity-100 hover:bg-red-500/10 transition-colors"
              style={{ color: textColor }}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      ) : (
        // Área de drop
        <div
          onClick={() => !disabled && inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            flex flex-col items-center justify-center
            p-8 border-2 border-dashed rounded-xl
            transition-all cursor-pointer
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={isDragOver
            ? { borderColor: accentColor, backgroundColor: `${accentColor}1A` }
            : error
              ? { borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.05)' }
              : { borderColor: `${textColor}4D`, backgroundColor: `${textColor}08` }
          }
        >
          <Upload
            className="w-10 h-10 mb-4"
            style={isDragOver ? { color: accentColor } : { color: textColor, opacity: 0.5 }}
          />
          {/* alpha no color (não opacity) — opacity do pai dimaria o span accent */}
          <p className="text-sm text-center" style={{ color: `${textColor}B3` }}>
            <span className="font-medium" style={{ color: accentColor }}>Clique para enviar</span>
            {' '}ou arraste um arquivo
          </p>
          <p className="text-xs mt-2" style={{ color: textColor, opacity: 0.5 }}>
            Máximo: {(maxSize / 1024 / 1024).toFixed(0)}MB
          </p>
        </div>
      )}
    </div>
  );
};
